import { useState } from "react";
import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { makeApi, aiErrorText, coinToast } from "@/lib/api";
import { KINDS, STEPS, STORY_TEXT } from "@/components/story/content";

export default function StoryWizard({ onScript }) {
  const { lang } = useT();
  const tx = STORY_TEXT[lang];
  const { getToken, isSignedIn } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ kind: "", topic: "", character: "", place: "", ending: "" });
  const [loading, setLoading] = useState(false);
  const key = STEPS[step];
  const filled = form[key].trim().length > 0;
  const last = step === STEPS.length - 1;

  const submit = async () => {
    setLoading(true);
    try {
      const r = await makeApi(getToken).post("/story/script", { ...form, language: lang }, { timeout: 90000 });
      coinToast(r.data.coins_awarded, lang);
      onScript({ title: r.data.title, scenes: r.data.scenes, coverPrompt: r.data.cover_prompt });
    } catch (e) {
      toast.error(aiErrorText(e, lang, tx.fail));
    } finally {
      setLoading(false);
    }
  };

  if (!isSignedIn) {
    return <Link to="/sign-in" className="glossy-btn inline-block bg-orange-500 text-white font-fredoka font-bold px-6 py-3 rounded-full" data-testid="story-login-link">{tx.login}</Link>;
  }

  return (
    <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-[2rem] shadow-lg p-5 sm:p-7" data-testid="story-wizard">
      <p className="text-xs font-bold uppercase tracking-wider text-teal-700" data-testid="story-step-label">{tx.step} {step + 1} {tx.of} {STEPS.length}</p>
      <div className="mt-2 h-2 rounded-full bg-teal-100 overflow-hidden"><div className="h-full bg-teal-500 transition-[width] duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
      <h3 className="font-fredoka text-2xl sm:text-3xl font-bold text-blue-950 mt-4">{tx.q[key]}</h3>
      {key === "kind" ? (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {KINDS.map((k) => (
            <button key={k.id} type="button" onClick={() => setForm({ ...form, kind: k.id })} data-testid={`story-kind-${k.id}`}
              className={`${k.color} rounded-2xl px-4 py-4 font-fredoka font-bold text-lg text-blue-950 border-4 transition-transform hover:-translate-y-1 ${form.kind === k.id ? "border-blue-950" : "border-transparent"}`}>
              {k[lang]}
            </button>
          ))}
        </div>
      ) : (
        <textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value.slice(0, 200) })} placeholder={tx.ph[key]} rows={3}
          className="mt-4 w-full rounded-2xl border-4 border-teal-200 focus:border-teal-500 outline-none p-4 text-lg font-semibold text-blue-950" data-testid={`story-input-${key}`} />
      )}
      <div className="mt-6 flex flex-wrap gap-3 justify-between">
        <button type="button" disabled={step === 0 || loading} onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 font-bold text-blue-900 disabled:opacity-30" data-testid="story-back-btn">
          <ArrowLeft className="w-4 h-4" /> {tx.back}
        </button>
        {last ? (
          <button type="button" disabled={!filled || loading} onClick={submit} className="glossy-btn bg-orange-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-50" data-testid="story-generate-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />} {loading ? tx.writing : tx.make}
          </button>
        ) : (
          <button type="button" disabled={!filled} onClick={() => setStep(step + 1)} className="glossy-btn bg-teal-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-50" data-testid="story-next-btn">
            {tx.next} <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
